import MuiAutocompleteField, {
  type AutocompleteProps,
} from '@mui/material/Autocomplete';
import { forwardRef } from 'react';
import TextField, { type InputProps } from './TextInput';

export type { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';

export type AutocompleteOption = {
  label: string;
  value: string | number;
};

type AutocompleteInputProps<T extends AutocompleteOption = AutocompleteOption> =
  Omit<
    AutocompleteProps<T, false, false, false>,
    'renderInput' | 'value' | 'options' | 'onChange'
  > & {
    value?: T['value'] | null;
    options: T[];
    onChange: (value: T['value'] | null) => void;
    error?: boolean;
    required?: boolean;
    helperText?: React.ReactNode;
    InputProps?: InputProps;
    label?: React.ReactNode;
    placeholder: string;
  };

function AutocompleteInputInner<T extends AutocompleteOption>(
  props: AutocompleteInputProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    value,
    options,
    onChange,
    error,
    helperText,
    InputProps,
    label,
    required,
    disabled,
    sx,
    ...restProps
  } = props;
  const selected = options.find((o) => o.value === value) ?? null;
  const fieldOnChange = (_: React.SyntheticEvent, option: T | null) => {
    const selectedValue = option ? option.value : null;
    onChange(selectedValue);
  };

  return (
    <MuiAutocompleteField
      ref={ref}
      value={selected}
      options={options}
      onChange={fieldOnChange}
      fullWidth
      disabled={disabled}
      sx={sx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          disabled={disabled}
          helperText={helperText}
          error={error}
          InputProps={{ ...params.InputProps, ...InputProps }}
        />
      )}
      {...restProps}
    />
  );
}

const AutocompleteInput = forwardRef(AutocompleteInputInner) as <
  T extends AutocompleteOption = AutocompleteOption,
>(
  props: AutocompleteInputProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => React.ReactElement;

export default AutocompleteInput;
